import React, { type MouseEvent, useMemo } from 'react';
import styles from './SVG.module.sass';

interface SVGProps {
  type: string;
  hidden?: boolean;
  onClick?: (event: MouseEvent) => void;
  withContainer?: boolean;
  className?: string;
}

/**
 * The SVG component is a wrapper for customized SVGs.
 * The wrapper div use the local class name `svg` to isolate the styles.
 * The SVG elements themself use global class name if needed to allow for global styling when
 * used in other components.
 */
const SVG: React.FC<SVGProps> = ({ type, hidden, onClick, withContainer = true }) => {
  const chooseSVG = useMemo(() => {
    switch (type) {
      case 'spinner':
        return (
          <svg
            width="40"
            height="40"
            viewBox="0 0 38 38"
            xmlns="http://www.w3.org/2000/svg"
            stroke="#0DBEBB"
            className="spinner"
          >
            <g fill="none" fillRule="evenodd">
              <g transform="translate(1 1)" strokeWidth="2">
                <circle stroke="#0F0F0F" cx="18" cy="18" r="18" />
                <path d="M36 18c0-9.94-8.06-18-18-18">
                  <animateTransform
                    attributeName="transform"
                    type="rotate"
                    from="0 18 18"
                    to="360 18 18"
                    dur="1s"
                    repeatCount="indefinite"
                  />
                </path>
              </g>
            </g>
          </svg>
        );
      case 'exit-fullscreen':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 469.333 469.333"
            height="18"
            width="18"
          >
            <path
              strokeWidth="30"
              fill="#fff"
              stroke="#fff"
              d="M160 0h-21.333A10.66 10.66 0 00128 10.667V128H10.667A10.66 10.66 0 000 138.667V160a10.66 10.66 0 0010.667 10.667H160A10.66 10.66 0 00170.667 160V10.667A10.66 10.66 0 00160 0zM458.667 128H341.333V10.667A10.66 10.66 0 00330.667 0h-21.333a10.66 10.66 0 00-10.667 10.667V160a10.66 10.66 0 0010.667 10.667h149.333A10.66 10.66 0 00469.334 160v-21.333A10.662 10.662 0 00458.667 128zM458.667 298.667H309.333a10.66 10.66 0 00-10.667 10.667v149.333a10.66 10.66 0 0010.667 10.667h21.333a10.66 10.66 0 0010.667-10.667V341.333h117.333a10.66 10.66 0 0010.667-10.667v-21.333a10.66 10.66 0 00-10.666-10.666zM160 298.667H10.667A10.66 10.66 0 000 309.333v21.333a10.66 10.66 0 0010.667 10.667H128v117.333a10.66 10.66 0 0010.667 10.667H160a10.66 10.66 0 0010.667-10.667V309.333A10.66 10.66 0 00160 298.667z"
            />
          </svg>
        );
      case 'fullscreen':
        return (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 298.667 298.667"
            height="18"
            width="18"
          >
            <path
              fill="#fff"
              d="M42.667 192H0v106.667h106.667V256h-64zM0 106.667h42.667v-64h64V0H0zM192 0v42.667h64v64h42.667V0zM256 256h-64v42.667h106.667V192H256z"
            />
          </svg>
        );
      case 'camera-icon':
        return (
          <svg viewBox="0 0 77 60" height="60" width="77" className="camera-icon">
            <g fill="none" fillRule="evenodd">
              <path
                fill="#A09FA0"
                d="M67.667 13.592H38.11v45.89l6.75-.823 5.955-2.338 4.406-2.504 3.219-2.728 2.726-2.594 2.934-3.633 1.79-4.16 1.776-4.98z"
              />
              <path
                fill="#BFBFBF"
                d="M8.556 13.592H38.11v45.89l-6.75-.823-5.955-2.338L21 53.817l-3.218-2.728-2.727-2.594-2.934-3.633-1.79-4.16-1.775-4.98zM72.333 4.26H38.111v9.332h31.722a2.5 2.5 0 002.5-2.5V4.26z"
              />
              <path d="M3.889 4.26H38.11v9.332H6.39a2.5 2.5 0 01-2.5-2.5V4.26z" fill="#A09FA0" />
              <path d="M75.056.37H38.11v3.89h36.945a1.944 1.944 0 100-3.89z" fill="#5D5D5D" />
              <path d="M1.944.37h36.167v3.89H1.944a1.944 1.944 0 010-3.89z" fill="#5D5D5D" />
              <circle fill="#3F3F3F" cx="34.611" cy="31.87" r="12.833" />
              <circle fill="#EAEAEA" cx="37.333" cy="29.926" r="5.444" />
              <ellipse
                fill="#3F3F3F"
                transform="rotate(30 46.923 46.023)"
                cx="46.923"
                cy="46.023"
                rx="3.889"
                ry="4.667"
              />
            </g>
          </svg>
        );
      case 'info-icon':
        return (
          <svg
            width="16"
            height="16"
            viewBox="0 0 12 12"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="info-icon"
            onClick={onClick}
          >
            <path
              d="M5.991 0a5.991 5.991 0 105.992 5.991A5.999 5.999 0 005.991 0zm0 10.894a4.902 4.902 0 110-9.805 4.902 4.902 0 010 9.805z"
              fill="#3A8481"
            />
            <path
              d="M5.991 4.357c-.3 0-.544.244-.544.545v3.813a.545.545 0 101.09 0V4.902c0-.3-.245-.545-.546-.545zM5.991 3.868a.6.6 0 100-1.2.6.6 0 000 1.2z"
              fill="#3A8481"
            />
          </svg>
        );
      case 'right-chevron':
        return (
          <svg
            className="right-chevron"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 407.436 407.436"
          >
            <path
              fill={hidden ? 'transparent' : '#FFF'}
              d="M112.814 0L91.566 21.178l181.946 182.54-181.946 182.54 21.248 21.178 203.055-203.718z"
            />
          </svg>
        );
      case 'left-chevron':
        return (
          <svg
            className="left-chevron"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 407.436 407.436"
          >
            <path
              fill={hidden ? 'transparent' : '#FFF'}
              d="M315.869 21.178L294.621 0 91.566 203.718l203.055 203.718 21.248-21.178-181.945-182.54z"
            />
          </svg>
        );
      case 'solink-logo':
        return (
          <svg
            className="solink-logo"
            viewBox="0 0 26 25"
            version="1.1"
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
          >
            <g id="solink-logo" fillRule="nonzero">
              <path
                className="arc"
                d="M16.88667,17.411 L21.05467,21.6096 C17.43597,24.7043 12.35447,25.3796 8.06267,23.3362 C8.21767,22.8661 8.29642,22.37391 8.29588,21.8786 C8.29560539,19.3669 6.27418,17.3309 3.78078,17.3309 C3.2852,17.3320861 2.793,17.413087 2.32278,17.57084 C0.28078,13.24204 0.94688,8.10954 4.02488,4.45484 L8.18788,8.64794 C6.39088,11.14464 6.66148,14.58674 8.82616,16.76744 C10.99086,18.94814 14.40776,19.22094 16.88646,17.41087 L16.88667,17.411 Z"
                fill="#FFFFFF"
              ></path>
              <path
                className="arc"
                d="M25.42367,12.42 C25.4280687,15.3063 24.40967,18.0995 22.55167,20.2968 L18.38467,16.0993 C20.18187,13.6031 19.91147,10.1614 17.74697,7.981 C15.58247,5.8006 12.16577,5.5283 9.68777,7.33863 L5.52077,3.14113 C9.11987,0.06513 14.16637,-0.62107 18.44677,1.38343 L18.44677,1.3889386 C17.86172,2.7935386 18.01265,4.3998386 18.84895,5.6690386 C19.68525,6.9381386 21.09615,7.7020386 22.60835,7.7043386 C23.19972,7.7063574 23.78555,7.5893886 24.33145,7.3602986 C25.05531,8.9472986 25.42815,10.6736986 25.42415,12.4201986 L25.42367,12.42 Z"
                fill="#FFFFFF"
              ></path>
              <path
                className="circle"
                d="M17.65667,12.255 C17.6611974,13.7496 16.88263,15.1396 15.60007,15.9264 C14.90815,16.35902 14.10607,16.58721 13.28797,16.58421 C10.87497,16.58421 8.91877,14.64601 8.91877,12.25501 C8.91794245,9.86291 10.87377,7.92281 13.28797,7.92091 C14.44747,7.92117188 15.55937,8.37806 16.37877,9.19101 C17.19816,10.00392 17.65787,11.10621 17.65667,12.25511 L17.65667,12.255 Z"
                fill="#FFFFFF"
              ></path>
              <path
                className="circle"
                d="M6.97737,21.905 C6.97752909,22.15263 6.94592,22.39929 6.883288,22.63918 C6.516998,24.11398 5.113388,25.10998 3.570488,24.98998 C2.027588,24.86996 0.801688,23.66938 0.676988,22.15638 C0.552348,20.64338 1.565858,19.26538 3.069088,18.90398 C3.315458,18.842797 3.568618,18.811804 3.822768,18.811709 C5.564468,18.8125344 6.976168,20.196909 6.977368,21.905009 L6.97737,21.905 Z"
                fill="#FFFFFF"
              ></path>
              <path
                className="circle"
                d="M25.66667,3.0946 C25.6674001,4.3852 24.87468,5.5399 23.67987,5.9886 C23.33898,6.12163 22.97664,6.18931 22.61137,6.18818 C22.51098,6.18847961 22.41066,6.1831492 22.31086,6.172213 C22.21851,6.1655994 22.12688,6.151078 22.03695,6.128804 C20.61755,5.832634 19.59895,4.567004 19.59885,3.099104 C19.5965651,2.732884 19.662241,2.369494 19.79246,2.027804 C20.19125,0.917304 21.18216,0.135704 22.34286,0.016004 C22.432272,0.00503 22.52227,-0.000303 22.61234,3.7e-05 C22.71633,-0.00025804 22.82026,0.0050718 22.92369,0.016004 C24.48139,0.179524 25.66549,1.508404 25.66669,3.094604 L25.66667,3.0946 Z"
                fill="#FFFFFF"
              ></path>
            </g>
          </svg>
        );
      case 'print':
        return (
          <svg viewBox="0 0 24 25" id="print" height="20px" width="20px">
            <path
              fill="#fff"
              d="M22.8 8.523h-3.6v-3.6a1.34 1.34 0 00-.35-.84l-3.6-3.6a1.352 1.352 0 00-.85-.36H6a1.2 1.2 0 00-1.2 1.2v7.2H1.2a1.2 1.2 0 00-1.2 1.2v9.6a1.2 1.2 0 001.2 1.2h3.6v2.4a1.2 1.2 0 001.2 1.2h12a1.2 1.2 0 001.2-1.2v-2.4h3.6a1.2 1.2 0 001.2-1.2v-9.6a1.2 1.2 0 00-1.2-1.2zm-6 13.2H7.2v-3.6h9.6v3.6zm0-13.2H7.2v-6h6v2.4a1.2 1.2 0 001.2 1.2h2.4v2.4z"
            />
          </svg>
        );
      case 'subtitles-off':
        return (
          <svg
            id="Capa_1"
            enableBackground="new 0 0 512 512"
            height="23"
            viewBox="0 0 512 512"
            width="23"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#fff"
              d="m0 37.92v350h177v86.16l143.599-86.16h191.401v-350h-512zm482 320h-169.71l-105.29 63.174v-63.174h-177v-290h452z"
            />
            <path
              fill="#fff"
              d="m185.023 294.816c27.577 0 50.408-9.946 65.327-28.255l-23.509-21.7c-10.624 12.433-23.96 18.762-39.784 18.762-29.611 0-50.634-20.796-50.634-50.634s21.022-50.634 50.634-50.634c15.823 0 29.16 6.329 39.784 18.536l23.509-21.7c-14.919-18.083-37.75-28.029-65.101-28.029-49.278 0-85.896 34.132-85.896 81.828s36.618 81.826 85.67 81.826z"
            />
            <path
              fill="#fff"
              d="m347.321 294.816c27.577 0 50.408-9.946 65.327-28.255l-23.509-21.7c-10.624 12.433-23.96 18.762-39.784 18.762-29.612 0-50.634-20.796-50.634-50.634s21.022-50.634 50.634-50.634c15.823 0 29.16 6.329 39.784 18.536l23.509-21.7c-14.919-18.083-37.749-28.029-65.101-28.029-49.278 0-85.897 34.132-85.897 81.828s36.619 81.826 85.671 81.826z"
            />
          </svg>
        );
      case 'subtitles':
        return (
          <svg
            width="23"
            height="23"
            viewBox="0 0 512 512"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect y="38" width="512" height="349" fill="white" />
            <path
              d="M0 37.92V387.92H177V474.08L320.599 387.92H512V37.92H0V37.92ZM482 357.92H312.29L207 421.094V357.92H30V67.92H482V357.92Z"
              fill="white"
            />
            <path
              d="M185.023 294.816C212.6 294.816 235.431 284.87 250.35 266.561L226.841 244.861C216.217 257.294 202.881 263.623 187.057 263.623C157.446 263.623 136.423 242.827 136.423 212.989C136.423 183.151 157.445 162.355 187.057 162.355C202.88 162.355 216.217 168.684 226.841 180.891L250.35 159.191C235.431 141.108 212.6 131.162 185.249 131.162C135.971 131.162 99.353 165.294 99.353 212.99C99.353 260.686 135.971 294.816 185.023 294.816V294.816ZM347.321 294.816C374.898 294.816 397.729 284.87 412.648 266.561L389.139 244.861C378.515 257.294 365.179 263.623 349.355 263.623C319.743 263.623 298.721 242.827 298.721 212.989C298.721 183.151 319.743 162.355 349.355 162.355C365.178 162.355 378.515 168.684 389.139 180.891L412.648 159.191C397.729 141.108 374.899 131.162 347.547 131.162C298.269 131.162 261.65 165.294 261.65 212.99C261.65 260.686 298.269 294.816 347.321 294.816Z"
              fill="black"
            />
            <rect x="200" y="382" width="65" height="39" fill="white" />
          </svg>
        );
      case 'audio':
        return (
          <svg
            height="20"
            viewBox="0 0 511.994 511.994"
            width="20"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fill="#fff"
              d="M458.101 419.899c-2.486 0-5.007-.619-7.329-1.922-7.224-4.056-9.792-13.199-5.737-20.423 24.18-43.071 36.96-92.024 36.96-141.566 0-49.537-12.778-98.485-36.953-141.553-4.055-7.224-1.486-16.368 5.738-20.422 7.223-4.056 16.368-1.485 20.422 5.738 26.686 47.543 40.793 101.569 40.793 156.238 0 54.675-14.108 108.706-40.8 156.252-2.753 4.9-7.847 7.658-13.094 7.658z"
            />
            <path
              fill="#fff"
              d="M403.893 377.176c-2.265 0-4.563-.515-6.725-1.602-7.402-3.72-10.386-12.737-6.665-20.139 15.617-31.069 23.536-64.528 23.536-99.448 0-34.914-7.917-68.369-23.53-99.435-3.72-7.402-.736-16.418 6.667-20.138 7.4-3.722 16.418-.736 20.138 6.667 17.733 35.284 26.725 73.271 26.725 112.907 0 39.642-8.994 77.634-26.732 112.922-2.635 5.24-7.924 8.266-13.414 8.266zM348.992 333.903a14.95 14.95 0 01-5.573-1.08c-7.69-3.081-11.427-11.813-8.346-19.503 7.306-18.236 11.01-37.525 11.01-57.333 0-19.802-3.703-39.087-11.004-57.32-3.08-7.69.658-16.422 8.348-19.501 7.69-3.081 16.421.658 19.501 8.348 8.729 21.796 13.155 44.834 13.155 68.474 0 23.646-4.428 46.689-13.161 68.489-2.349 5.861-7.98 9.426-13.93 9.426zM133.359 163.267H45c-24.813 0-45 20.187-45 45v95.46c0 24.813 20.187 45 45 45h85.328zM295.974 59.859a15.001 15.001 0 00-15.871 1.813L163.497 155.05l-3.257 199.286 119.863 95.987a14.997 14.997 0 0015.871 1.813 14.998 14.998 0 008.505-13.521V73.38a14.998 14.998 0 00-8.505-13.521z"
            />
          </svg>
        );
      case 'audio-muted':
        return (
          <svg
            width="20"
            height="20"
            viewBox="0 0 512 512"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clipPath="url(#clip0)">
              <path
                d="M133.361 163.269H45.0005C20.1872 163.269 0 183.456 0 208.269V303.731C0 328.544 20.1872 348.731 45.0005 348.731H130.33L133.361 163.269Z"
                fill="#fff"
              />
              <path
                d="M295.977 59.8597C290.779 57.3627 284.608 58.0677 280.106 61.6727L163.499 155.052L160.242 354.34L280.106 450.328C282.819 452.5 286.137 453.62 289.485 453.62C291.692 453.62 293.911 453.133 295.977 452.141C301.177 449.644 304.483 444.388 304.483 438.62V73.3809C304.483 67.6128 301.177 62.3567 295.977 59.8597V59.8597Z"
                fill="#fff"
              />
              <line x1="8.53191" y1="500.498" x2="475" y2="50" stroke="#fff" strokeWidth="24" />
            </g>
            <defs>
              <clipPath id="clip0">
                <rect width="512" height="512" fill="white" />
              </clipPath>
            </defs>
          </svg>
        );
      default:
        return null;
    }
  }, [type, hidden, onClick]);

  return withContainer ? <div className={styles.svg}>{chooseSVG}</div> : chooseSVG;
};

export default SVG;
